import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as endpoints from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { useCart } from "@/cart/CartContext";
import { formatYen } from "@/utils/format";

export function CartPage() {
  const { cart, loading, refresh, setCart } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState<endpoints.CartCsvImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setItemBusy = (pid: number, v: boolean) =>
    setBusy((prev) => ({ ...prev, [pid]: v }));

  const onUpdate = async (productId: number, quantity: number) => {
    setError(null);
    setItemBusy(productId, true);
    try {
      const res = await endpoints.updateCartItem(productId, quantity);
      setCart(res);
    } catch (e) {
      setError(apiErrorMessage(e, "数量変更に失敗しました"));
    } finally {
      setItemBusy(productId, false);
    }
  };

  const onRemove = async (productId: number) => {
    setError(null);
    setItemBusy(productId, true);
    try {
      const res = await endpoints.removeCartItem(productId);
      setCart(res);
    } catch (e) {
      setError(apiErrorMessage(e, "削除に失敗しました"));
    } finally {
      setItemBusy(productId, false);
    }
  };

  const onClear = async () => {
    if (!window.confirm("カートをすべて空にしますか？")) return;
    setClearing(true);
    setError(null);
    try {
      const res = await endpoints.clearCart();
      setCart(res);
    } catch (e) {
      setError(apiErrorMessage(e, "カートのクリアに失敗しました"));
    } finally {
      setClearing(false);
    }
  };

  const onCsvSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    setError(null);
    setCsvResult(null);
    try {
      const res = await endpoints.importCartCsv(file);
      setCsvResult(res);
      setCart(res.cart);
    } catch (err) {
      setError(apiErrorMessage(err, "CSV 取込に失敗しました"));
    } finally {
      setCsvImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading && cart.items.length === 0) {
    return <div className="text-center py-12 text-gray-500">読み込み中...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">カート</h1>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => void onCsvSelected(e)}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={csvImporting}
          >
            {csvImporting ? "取込中..." : "📂 CSV で一括追加"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void refresh()}
            disabled={loading}
          >
            再読込
          </button>
          {cart.items.length > 0 && (
            <button
              type="button"
              className="btn-danger"
              onClick={onClear}
              disabled={clearing}
            >
              {clearing ? "削除中..." : "全削除"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {csvResult && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">CSV 取込結果</h2>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-700 text-lg leading-none"
              onClick={() => setCsvResult(null)}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="badge-green">追加 {csvResult.added}</span>
            <span className="badge-blue">更新 {csvResult.updated}</span>
            {csvResult.skipped > 0 && (
              <span className="badge-red">エラー {csvResult.skipped}</span>
            )}
          </div>
          {csvResult.errors.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">行</th>
                    <th className="px-2 py-1 text-left">JAN</th>
                    <th className="px-2 py-1 text-left">理由</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {csvResult.errors.map((err) => (
                    <tr key={`${err.row}-${err.jan}`}>
                      <td className="px-2 py-1 text-gray-500">{err.row}</td>
                      <td className="px-2 py-1 font-mono">{err.jan || "(空)"}</td>
                      <td className="px-2 py-1 text-red-700">{err.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-2">
                エラー行はカートに追加されていません。CSV を修正して再アップロードしてください。
              </p>
            </div>
          )}
        </div>
      )}

      {cart.items.length === 0 ? (
        <div className="card p-8 text-center space-y-3">
          <p className="text-gray-600">カートに商品がありません。</p>
          <Link to="/catalog" className="btn-primary inline-block">
            カタログを見る
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-3">
            {cart.items.map((item) => {
              const p = item.product;
              if (!p) return null;
              const unit = p.order_unit || 1;
              return (
                <div key={item.id} className="card p-4 flex gap-4">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.product_name}
                      className="w-24 h-24 rounded bg-gray-100 object-cover"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                      画像なし
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{p.product_name}</h3>
                    <p className="text-xs text-gray-500">{p.jan}</p>
                    <div className="mt-2 text-sm text-gray-600">
                      単価 {formatYen(item.tax_inclusive_unit_price ?? 0)}
                      <span className="text-xs text-gray-500 ml-1">(税込)</span>
                      ／ 発注単位 {unit} 個
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        className="btn-secondary px-2 py-1"
                        disabled={busy[item.product_id] || item.quantity <= unit}
                        onClick={() =>
                          void onUpdate(item.product_id, Math.max(unit, item.quantity - unit))
                        }
                      >
                        −
                      </button>
                      <span className="w-12 text-center">{item.quantity}</span>
                      <button
                        type="button"
                        className="btn-secondary px-2 py-1"
                        disabled={busy[item.product_id]}
                        onClick={() => void onUpdate(item.product_id, item.quantity + unit)}
                      >
                        ＋
                      </button>
                      <button
                        type="button"
                        className="btn-danger ml-auto"
                        disabled={busy[item.product_id]}
                        onClick={() => void onRemove(item.product_id)}
                      >
                        削除
                      </button>
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-xs text-gray-500">小計 (税込)</div>
                    <div className="text-lg font-bold">{formatYen(item.subtotal)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            <div className="card p-4 sticky top-20">
              <h2 className="font-semibold mb-3">注文サマリー</h2>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">商品点数</span>
                <span>{cart.item_count} 件</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">商品代金 (税込)</span>
                <span>{formatYen(cart.total_amount)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">送料</span>
                <span className="text-green-700 font-medium">無料</span>
              </div>
              <div className="flex justify-between items-baseline mb-4 pt-3 border-t">
                <span className="text-gray-600">合計 (税込)</span>
                <span className="text-2xl font-bold">{formatYen(cart.total_amount)}</span>
              </div>
              <button
                type="button"
                className="btn-primary w-full"
                onClick={() => navigate("/checkout")}
              >
                発注手続きへ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
